import { useState, useCallback, useRef, useEffect } from 'react';
import { message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { useBundle } from '@amzn/react-arb-tools';
import { recordUIEvent, safeGetMessage, GENERAL_ERROR } from '@xbcb/ui-utils';
import { RumUIEvent } from '@xbcb/ui-types';
import { DEEP_THOUGHT_RUM_EVENT_PROGRAM_NAME } from 'libs/constant';
import { formatHTSNumber } from 'libs/format';
import type { FormInstance } from 'antd';
import type { ClassificationState } from 'components/LineItemTariffs/HtsClassification/types';
import {
  InvokeItemClassificationApiDocument,
  ItemClassificationOperation,
  type InvokeItemClassificationApiMutationVariables,
} from '__generated__/graphql';
import { ProductSecondaryIdentifierType } from '@xbcb/compliance-types';
import { GET_PRODUCT } from 'libs/productHelpers/getProduct';

// Extracts the innermost user-facing "message" from DeepThought's nested error JSON.
// Falls back to GENERAL_ERROR if the error string cannot be parsed.
export const extractUserFacingError = (
  rawError: string | null | undefined,
): string => {
  if (!rawError) return GENERAL_ERROR;
  try {
    const matches = [
      ...rawError.matchAll(/\\?"message\\?"\s*:\s*\\?"([^\\"]+)\\?"/g),
    ];
    const lastMatch = matches[matches.length - 1];
    if (lastMatch?.[1]) return lastMatch[1];
  } catch {
    // parsing failed
  }
  return GENERAL_ERROR;
};

interface UseHtsClassificationParams {
  description: string;
  destinationCountryCode: string;
  entryId: string;
  lineIndex: number;
  userId: string;
  form: FormInstance;
  fullNamePath: (string | number)[];
  productId?: string;
  tariffs?: { htsNumber?: string }[];
}

export function useHtsClassification({
  description,
  destinationCountryCode,
  entryId,
  lineIndex,
  userId,
  form,
  fullNamePath,
  productId,
  tariffs,
}: UseHtsClassificationParams) {
  const [invokeClassification] = useMutation(
    InvokeItemClassificationApiDocument,
  );
  const [bundle] = useBundle('components.HtsClassification');
  const { data: productData } = useQuery(GET_PRODUCT, {
    variables: { id: productId },
    skip: !productId,
  });
  const [validateLoading, setValidateLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [mountLoading, setMountLoading] = useState(false);
  const [classificationState, setClassificationStateRaw] =
    useState<ClassificationState>({ selectedAnswers: {} });

  const setClassificationState = useCallback(
    (
      update:
        | ClassificationState
        | ((prev: ClassificationState) => ClassificationState),
    ) => {
      setClassificationStateRaw((prev) => {
        const next = typeof update === 'function' ? update(prev) : update;
        form.setFields([
          { name: [...fullNamePath, 'classificationCache'], value: next },
        ]);
        return next;
      });
    },
    [form, fullNamePath],
  );
  const drawerOpenedRef = useRef(false);
  const loadedRef = useRef(false);
  const programmaticUpdateRef = useRef(false);
  const initializedRef = useRef(false);
  const prevPrimaryHtsRef = useRef<string | undefined>();
  const prevDescriptionRef = useRef<string | undefined>();

  const updateComplianceCheckStatus = useCallback(
    (status: string | null) => {
      const current =
        form.getFieldValue([...fullNamePath, 'complianceChecks']) ?? [];
      const idx = [...current]
        .reverse()
        .findIndex((c: { type: string }) => c.type === 'HTS_CLASSIFICATION');
      if (idx === -1) return;
      const realIdx = current.length - 1 - idx;
      form.setFields([
        {
          name: [...fullNamePath, 'complianceChecks'],
          value: current.map(
            (
              c: { requestId: string; type: string; status?: string },
              i: number,
            ) => (i === realIdx ? { ...c, status } : c),
          ),
        },
      ]);
    },
    [form, fullNamePath],
  );

  // Load existing classification state on mount
  useEffect(() => {
    if (loadedRef.current) return;

    const complianceChecks =
      form.getFieldValue([...fullNamePath, 'complianceChecks']) ?? [];
    const existing = [...complianceChecks]
      .reverse()
      .find((c: { type: string }) => c.type === 'HTS_CLASSIFICATION');

    // 1. If persisted status exists, restore from status (skip cache)
    if (existing?.status) {
      loadedRef.current = true;

      // ACCEPTED — static message, no API call
      if (existing.status === 'ACCEPTED') {
        setClassificationState({
          requestId: existing.requestId,
          selectedAnswers: {},
          confirmationAlert: {
            type: 'success',
            message:
              safeGetMessage(bundle, 'hts_number_updated') ||
              'HTS number is successfully updated.',
          },
        });
        return;
      }

      // DECLINED / AI_VALIDATED — need API call for tariffCode / reasoningTrace
      if (
        existing.status === 'DECLINED' ||
        existing.status === 'AI_VALIDATED'
      ) {
        void (async () => {
          setMountLoading(true);
          try {
            const result = await invokeClassification({
              variables: {
                input: {
                  apiName: ItemClassificationOperation.GetClassificationRequest,
                  apiArgs: {
                    classificationRequestId: existing.requestId,
                    userId,
                    clientId: 'CBMSCustomsUI',
                  },
                },
              },
            });
            const response = result.data?.invokeItemClassificationApi?.response;
            if (!response?.result) return;

            const tariffCode = formatHTSNumber(response.result.tariffCode);

            if (existing.status === 'AI_VALIDATED') {
              setClassificationState({
                requestId: existing.requestId,
                selectedAnswers: {},
                confirmationAlert: {
                  type: 'success',
                  message:
                    safeGetMessage(
                      bundle,
                      'hts_number_validated',
                      undefined,
                      'HTS number is successfully validated by AI.',
                    ) || 'HTS number is successfully validated by AI.',
                  reasoningTrace: response.result.reasoningTrace,
                  confidence: response.result.confidence,
                },
              });
            } else {
              setClassificationState({
                requestId: existing.requestId,
                selectedAnswers: {},
                confirmationAlert: {
                  type: 'info',
                  message:
                    safeGetMessage(bundle, 'hts_number_declined', {
                      tariffCode,
                    }) || `Recommendation for ${tariffCode} was declined.`,
                },
              });
            }
          } catch {
            // Silent fail — show nothing on error
          } finally {
            setMountLoading(false);
          }
        })();
        return;
      }

      // USER_MANUAL_OVERRIDE — show nothing
      return;
    }

    // 2. No persisted status — check cache for in-progress session restore
    const cached = form.getFieldValue([...fullNamePath, 'classificationCache']);
    if (cached) {
      loadedRef.current = true;
      setClassificationState(cached);
      return;
    }

    // 3. No cache — fetch from API to restore pending questions or result
    if (!existing?.requestId) return;
    loadedRef.current = true;

    void (async () => {
      setMountLoading(true);
      try {
        const result = await invokeClassification({
          variables: {
            input: {
              apiName: ItemClassificationOperation.GetClassificationRequest,
              apiArgs: {
                classificationRequestId: existing.requestId,
                userId,
                clientId: 'CBMSCustomsUI',
              },
            },
          },
        });
        const { data } = result;
        const response = data?.invokeItemClassificationApi?.response;
        if (!response) return;

        let newState: ClassificationState | undefined;

        if (response.status === 'COMPLETE' && response.result) {
          newState = {
            requestId: existing.requestId,
            result: {
              tariffCode: formatHTSNumber(response.result.tariffCode),
              confidence: response.result.confidence,
              reasoningTrace: response.result.reasoningTrace,
            },
            selectedAnswers: {},
          };
        } else if (response.questions?.length) {
          const selectedAnswers: Record<string, string[]> = {};
          response.selectedAnswers?.forEach(
            (a: { questionId: string; answerId: string[] }) => {
              selectedAnswers[a.questionId] = a.answerId;
            },
          );
          newState = {
            requestId: existing.requestId,
            questions: response.questions,
            selectedAnswers,
          };
        }

        if (newState) {
          setClassificationState(newState);
        }
      } catch {
        // Silently fail — user can still start a new classification
      } finally {
        setMountLoading(false);
      }
    })();
  }, [form, fullNamePath, invokeClassification, userId]);

  // Invalidate classification when user manually edits primary HTS or description
  useEffect(() => {
    const primaryHts = tariffs?.[tariffs.length - 1]?.htsNumber;

    if (tariffs === undefined && description === undefined) return;

    if (!initializedRef.current) {
      initializedRef.current = true;
      prevPrimaryHtsRef.current = primaryHts;
      prevDescriptionRef.current = description;
      return;
    }

    if (programmaticUpdateRef.current) {
      programmaticUpdateRef.current = false;
      prevPrimaryHtsRef.current = primaryHts;
      prevDescriptionRef.current = description;
      return;
    }

    const htsChanged = primaryHts !== prevPrimaryHtsRef.current;
    const descChanged = description !== prevDescriptionRef.current;

    if (htsChanged || descChanged) {
      setClassificationStateRaw((prev) => {
        if (!prev.confirmationAlert) return prev;
        updateComplianceCheckStatus('USER_MANUAL_OVERRIDE');
        const next = { ...prev, confirmationAlert: undefined };
        form.setFields([
          { name: [...fullNamePath, 'classificationCache'], value: next },
        ]);
        return next;
      });
    }

    prevPrimaryHtsRef.current = primaryHts;
    prevDescriptionRef.current = description;
  }, [tariffs, description, form, fullNamePath, updateComplianceCheckStatus]);

  const invokeClassificationApi = useCallback(
    async (
      variables: InvokeItemClassificationApiMutationVariables,
      setLoading: (v: boolean) => void,
    ) => {
      setLoading(true);
      try {
        const { data } = await invokeClassification({ variables });
        return data?.invokeItemClassificationApi;
      } finally {
        setLoading(false);
      }
    },
    [invokeClassification],
  );

  const handleResponse = useCallback(
    (response: any, keepDrawerOpen = false) => {
      if (!response) return;
      let complianceChecks =
        form.getFieldValue([...fullNamePath, 'complianceChecks']) ?? [];
      if (response.classificationRequestId) {
        const alreadyExists = complianceChecks.some(
          (c: { requestId: string }) =>
            c.requestId === response.classificationRequestId,
        );
        if (!alreadyExists) {
          complianceChecks = [
            ...complianceChecks,
            {
              requestId: response.classificationRequestId,
              type: 'HTS_CLASSIFICATION',
            },
          ];
          form.setFields([
            {
              name: [...fullNamePath, 'complianceChecks'],
              value: complianceChecks,
            },
          ]);
        }
      }
      if (response.status === 'COMPLETE' && response.result) {
        const { tariffCode, confidence, reasoningTrace } = response.result;
        const formattedTariffCode = formatHTSNumber(tariffCode);
        const currentTariffs =
          form.getFieldValue([...fullNamePath, 'tariffs']) ?? [];
        const currentHts = currentTariffs[currentTariffs.length - 1]?.htsNumber;

        if (currentHts && formatHTSNumber(currentHts) === formattedTariffCode) {
          // AI returned the same HTS the user already has — mark as AI_VALIDATED
          // on the most recent HTS_CLASSIFICATION compliance check entry
          const revIdx = [...complianceChecks]
            .reverse()
            .findIndex(
              (c: { type: string }) => c.type === 'HTS_CLASSIFICATION',
            );
          if (revIdx !== -1) {
            const realIdx = complianceChecks.length - 1 - revIdx;
            form.setFields([
              {
                name: [...fullNamePath, 'complianceChecks'],
                value: complianceChecks.map(
                  (
                    c: { requestId: string; type: string; status?: string },
                    i: number,
                  ) => (i === realIdx ? { ...c, status: 'AI_VALIDATED' } : c),
                ),
              },
            ]);
          }
          setClassificationState((prev) => ({
            ...prev,
            questions: undefined,
            result: undefined,
            confirmationAlert: {
              type: 'success',
              message: safeGetMessage(
                bundle,
                'hts_number_validated',
                undefined,
                'HTS number is successfully validated by AI.',
              ),
              reasoningTrace,
              confidence,
            },
            drawerOpen: false,
          }));
        } else {
          setClassificationState((prev) => ({
            ...prev,
            result: {
              tariffCode: formattedTariffCode,
              confidence,
              reasoningTrace,
            },
            confirmationAlert: undefined,
            drawerOpen: keepDrawerOpen,
          }));
        }
      } else if (response.questions?.length) {
        setClassificationState({
          requestId: response.classificationRequestId,
          questions: response.questions,
          selectedAnswers: {},
          confirmationAlert: undefined,
          drawerOpen: keepDrawerOpen,
        });
      }
    },
    [form, fullNamePath, bundle, updateComplianceCheckStatus],
  );

  const handleOpenDrawer = useCallback(() => {
    if (!drawerOpenedRef.current) {
      recordUIEvent(RumUIEvent.USER_INTERACTION, {
        action: 'hts_classification_additional_info_requested',
        programName: DEEP_THOUGHT_RUM_EVENT_PROGRAM_NAME,
        entryId,
        lineIndex,
      });
      drawerOpenedRef.current = true;
    }
    setClassificationState((prev) => ({ ...prev, drawerOpen: true }));
  }, [entryId, lineIndex]);

  const handleCloseDrawer = useCallback(() => {
    setClassificationState((prev) => ({ ...prev, drawerOpen: false }));
  }, []);

  const asin = productData?.product?.secondaryIdentifiers?.find(
    (si: { type: ProductSecondaryIdentifierType }) =>
      si.type === ProductSecondaryIdentifierType.ASIN,
  )?.value;

  const createClassificationRequest = useCallback(
    async (setLoading: (v: boolean) => void) => {
      const payload = await invokeClassificationApi(
        {
          input: {
            apiName: ItemClassificationOperation.CreateClassificationRequest,
            apiArgs: {
              userId,
              clientId: 'CBMSCustomsUI',
              product: {
                name: description,
                description,
                asin,
              },
              interactionType: 'INTERACTIVE',
              countryCode: destinationCountryCode || 'US',
            },
          },
        },
        setLoading,
      );
      if (payload?.error) {
        return { response: null, error: payload.error as string };
      }
      return { response: payload?.response, error: null };
    },
    [
      description,
      destinationCountryCode,
      userId,
      invokeClassificationApi,
      asin,
    ],
  );

  const handleValidateHTS = useCallback(async () => {
    if (!description) {
      void message.error(
        safeGetMessage(bundle, 'hts_error_populate_description'),
      );
      return;
    }

    recordUIEvent(RumUIEvent.USER_INTERACTION, {
      action: 'hts_validate_button_clicked',
      programName: DEEP_THOUGHT_RUM_EVENT_PROGRAM_NAME,
      entryId,
      lineIndex,
    });

    try {
      const { response, error } = await createClassificationRequest(
        setValidateLoading,
      );
      if (error) {
        void message.error(extractUserFacingError(error));
        return;
      }
      if (response) handleResponse(response);
    } catch {
      void message.error(
        safeGetMessage(bundle, 'hts_error_classification_failed'),
      );
    }
  }, [
    description,
    entryId,
    lineIndex,
    createClassificationRequest,
    handleResponse,
  ]);

  const handleSubmitAnswers = useCallback(async () => {
    const hasUnanswered = classificationState.questions?.some(
      (q) => !(classificationState.selectedAnswers[q.id]?.length > 0),
    );
    if (hasUnanswered) {
      setClassificationState((prev) => ({
        ...prev,
        error: safeGetMessage(bundle, 'hts_error_answer_all_questions'),
      }));
      return;
    }

    setClassificationState((prev) => ({ ...prev, error: undefined }));
    const answers = Object.entries(classificationState.selectedAnswers).map(
      ([questionId, answerIds]) => ({ questionId, answerId: answerIds }),
    );
    try {
      const payload = await invokeClassificationApi(
        {
          input: {
            apiName: ItemClassificationOperation.SubmitClassificationAnswers,
            apiArgs: {
              classificationRequestId: classificationState.requestId,
              userId,
              clientId: 'CBMSCustomsUI',
              answers,
            },
          },
        },
        setSubmitLoading,
      );
      if (payload?.error) {
        setClassificationState((prev) => ({
          ...prev,
          error: extractUserFacingError(payload.error),
        }));
        return;
      }
      handleResponse(payload?.response, true);
    } catch {
      setClassificationState((prev) => ({
        ...prev,
        error: safeGetMessage(bundle, 'hts_error_submit_failed'),
      }));
    }
  }, [classificationState, userId, invokeClassificationApi, handleResponse]);

  const handleRestartClassification = useCallback(async () => {
    drawerOpenedRef.current = false;
    setClassificationState((prev) => ({ ...prev, error: undefined }));
    try {
      const { response, error } = await createClassificationRequest(
        setValidateLoading,
      );
      if (error) {
        setClassificationState((prev) => ({ ...prev, error }));
        return;
      }
      if (response) handleResponse(response, true);
    } catch {
      setClassificationState((prev) => ({
        ...prev,
        error: safeGetMessage(bundle, 'hts_error_classification_failed'),
      }));
    }
  }, [createClassificationRequest, handleResponse]);

  const handleAnswerChange = useCallback(
    (questionId: string, value: string[]) => {
      setClassificationState((prev) => ({
        ...prev,
        selectedAnswers: {
          ...prev.selectedAnswers,
          [questionId]: value,
        },
      }));
    },
    [],
  );

  const handleAcceptRecommendation = useCallback(() => {
    if (classificationState.result?.tariffCode) {
      recordUIEvent(RumUIEvent.USER_INTERACTION, {
        action: 'hts_recommendation_accepted',
        programName: DEEP_THOUGHT_RUM_EVENT_PROGRAM_NAME,
        entryId,
        lineIndex,
        recommendedHts: classificationState.result.tariffCode,
      });
      programmaticUpdateRef.current = true;
      form.setFields([
        {
          name: [...fullNamePath, 'tariffs'],
          value: [{ htsNumber: classificationState.result.tariffCode }],
        },
      ]);
      updateComplianceCheckStatus('ACCEPTED');
      setClassificationState({
        selectedAnswers: {},
        confirmationAlert: {
          type: 'success',
          message: safeGetMessage(bundle, 'hts_number_updated'),
          confidence: classificationState.result?.confidence,
        },
      });
    }
  }, [
    classificationState.result,
    updateComplianceCheckStatus,
    form,
    fullNamePath,
    entryId,
    lineIndex,
  ]);

  const handleDeclineRecommendation = useCallback(() => {
    const tariffCode = classificationState.result?.tariffCode;
    recordUIEvent(RumUIEvent.USER_INTERACTION, {
      action: 'hts_recommendation_declined',
      programName: DEEP_THOUGHT_RUM_EVENT_PROGRAM_NAME,
      entryId,
      lineIndex,
      recommendedHts: tariffCode,
    });
    updateComplianceCheckStatus('DECLINED');
    setClassificationState({
      selectedAnswers: {},
      confirmationAlert: {
        type: 'info',
        message: safeGetMessage(bundle, 'hts_number_declined', { tariffCode }),
      },
    });
  }, [
    classificationState.result,
    updateComplianceCheckStatus,
    entryId,
    lineIndex,
  ]);

  return {
    classificationState,
    validateLoading,
    submitLoading,
    mountLoading,
    handlers: {
      handleValidateHTS,
      handleSubmitAnswers,
      handleRestartClassification,
      handleAnswerChange,
      handleAcceptRecommendation,
      handleDeclineRecommendation,
      handleOpenDrawer,
      handleCloseDrawer,
    },
  };
}
